/**
 * AppointmentBlock — §31 ADR-115 canonical block.
 *
 * Mini-cards canônicos do painel direito (Brief 2A r7b `.mini-card`):
 * head (calendário + título + LifecycleBadge) + detail (quando) + metas
 * (endereço, técnico) + ações Ver agenda / Reagendar / Confirmar (tentative).
 */

import { Building2, Calendar, Repeat, User } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { AppointmentRescheduleModal } from '../../../../components/AppointmentRescheduleModal';
import { useMarkNoShowAppointment, useUpdateAppointment } from '../../../../hooks/use-appointments';
import { useToastStore } from '../../../../stores/toast-store';
import { NoShowModal } from '../../../appointments/NoShowModal';

import { LifecycleBadge } from './LifecycleBadge';

import type { AppointmentFixture } from '../../hooks/use-panel-fixtures';

export function AppointmentBlock({
  items,
  disabled = false,
  focusedEpisodeId = null,
  onFocusHere,
  focusPending = false,
}: {
  items: AppointmentFixture[];
  /** §2.8 — em conversa terminal todas as ações ficam disabled (read-only). */
  disabled?: boolean;
  /** ADR-155 Fase 4 (§3.1) — `conversation.episodeRefId`. `null` = sem foco. */
  focusedEpisodeId?: string | null;
  /** Presente → mostra a ação "Focar coleta aqui" nos não-focados/não-terminais. */
  onFocusHere?: (appointmentId: string) => void;
  /** Mutation do foco em curso — desabilita a ação p/ evitar duplo clique. */
  focusPending?: boolean;
}): JSX.Element {
  const updateAppointment = useUpdateAppointment();
  const markNoShow = useMarkNoShowAppointment();
  const navigate = useNavigate();
  const [rescheduleTarget, setRescheduleTarget] = useState<AppointmentFixture | null>(null);
  const [noShowTarget, setNoShowTarget] = useState<AppointmentFixture | null>(null);

  if (items.length === 0) {
    return (
      <div className="empty-mini">
        <Calendar className="icon" size={14} aria-hidden />
        Nenhum agendamento vinculado.
      </div>
    );
  }

  return (
    <>
      {items.map((a) => (
        <article key={a.id} className="mini-card" data-testid={`appointment-${a.id}`}>
          <div className="mini-card__head">
            <Calendar className="icon" size={14} aria-hidden />
            <span className="mini-card__title">{a.title}</span>
            {a.isSeries && (
              <span
                title="Parte de uma série recorrente"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 3,
                  marginLeft: 4,
                  fontSize: 10,
                  fontFamily: 'var(--font-mono)',
                  color: 'var(--accent-fg)',
                }}
              >
                <Repeat size={10} aria-hidden /> Série
              </span>
            )}
            <LifecycleBadge state={a.state} />
            {/* ADR-155 Fase 4 (§3.1) — episódio em foco desta conversa (D4). */}
            {a.id === focusedEpisodeId && (
              <span
                title="Este é o agendamento em foco — a Coleta desta conversa grava nele."
                data-testid={`appointment-focus-chip-${a.id}`}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 3,
                  marginLeft: 4,
                  fontSize: 10,
                  fontFamily: 'var(--font-mono)',
                  color: 'var(--info-fg)',
                }}
              >
                ● em foco
              </span>
            )}
          </div>

          <div className="mini-card__detail">
            {a.when}
            {a.isImminent && (
              <span
                style={{
                  marginLeft: 6,
                  fontSize: 10,
                  color: 'var(--warning-fg)',
                  fontFamily: 'var(--font-mono)',
                }}
              >
                · iminente
              </span>
            )}
          </div>

          {a.address.length > 0 && (
            <div className="mini-card__meta">
              <Building2 className="icon" size={14} aria-hidden /> {a.address}
            </div>
          )}
          {/* ADR-155 Fase 4 (§3.1) — contagem do envelope episódico; o detalhe
              completo (valor/proveniência) fica no Drawer/agenda. */}
          {a.collectedCount > 0 && (
            <div className="mini-card__meta" data-testid={`appointment-collected-count-${a.id}`}>
              {a.collectedCount} dado(s) desta consulta
            </div>
          )}
          {/* Frente B Sessão 2 — "quem atende" = recurso(s) no mundo recurso-cêntrico
              (não quem criou). Recai em `tech` (legado) quando não há recurso nativo. */}
          {a.resourceNames.length > 0 ? (
            <div className="mini-card__meta" data-testid={`appointment-resources-${a.id}`}>
              <User className="icon" size={14} aria-hidden /> {a.resourceNames.join(', ')}
            </div>
          ) : (
            a.tech.length > 0 && (
              <div className="mini-card__meta">
                <User className="icon" size={14} aria-hidden /> {a.tech}
              </div>
            )
          )}

          <div className="mini-card__actions">
            <button
              type="button"
              className="btn btn--ghost btn--xs"
              onClick={() => navigate('/agenda')}
              disabled={disabled}
            >
              Ver agenda
            </button>
            {/* Um agendamento TERMINAL (realizado, cancelado, falta) não muda mais de
                estado: reagendar ou concluir o que já acabou seria 422 no servidor.
                Antes desta rodada «Ver agenda» e «Reagendar» apareciam nos terminais
                também — becos sem saída na cara do operador. Ler o histórico continua. */}
            {!a.isTerminal && (
              <>
                <button
                  type="button"
                  className="btn btn--ghost btn--xs"
                  onClick={() => setRescheduleTarget(a)}
                  disabled={disabled}
                >
                  Reagendar
                </button>
                {/* O desfecho acontece AQUI, com a conversa aberta e o cliente na
                    frente (spec §5.4). Antes só existia na tela Agenda: o operador
                    ouvia «não vou poder ir» e precisava sair da conversa. */}
                <button
                  type="button"
                  /* Em `tentative` o passo natural é CONFIRMAR, não concluir — dois
                     botões primários lado a lado competiam pelo mesmo clique. Aqui
                     «Realizada» recua para secundário e volta a primário depois que
                     o agendamento existe de fato. */
                  className={
                    a.state === 'tentative' ? 'btn btn--ghost btn--xs' : 'btn btn--primary btn--xs'
                  }
                  onClick={() =>
                    updateAppointment.mutate({
                      appointmentId: a.id,
                      expectedVersion: a.version,
                      body: { status: 'completed' },
                    })
                  }
                  disabled={disabled || updateAppointment.isPending}
                >
                  ✓ Realizada
                </button>
                <button
                  type="button"
                  className="btn btn--ghost btn--xs"
                  onClick={() => setNoShowTarget(a)}
                  disabled={disabled}
                >
                  Faltou
                </button>
              </>
            )}
            {a.state === 'tentative' && (
              <button
                type="button"
                className="btn btn--primary btn--xs"
                onClick={() =>
                  updateAppointment.mutate({
                    appointmentId: a.id,
                    expectedVersion: a.version,
                    body: { status: 'confirmed' },
                  })
                }
                disabled={disabled || updateAppointment.isPending}
              >
                ✓ Confirmar
              </button>
            )}
            {/* ADR-155 Fase 4 (E3/§3.1) — seleção manual do episódio em foco:
                só nos não-focados e não-terminais (focar um terminal seria
                422 permanente no servidor). */}
            {onFocusHere !== undefined && a.id !== focusedEpisodeId && !a.isTerminal && (
              <button
                type="button"
                className="btn btn--ghost btn--xs"
                onClick={() => onFocusHere(a.id)}
                disabled={disabled || focusPending}
                data-testid={`appointment-focus-here-${a.id}`}
                title="A Coleta desta conversa passa a gravar neste agendamento."
              >
                Focar coleta aqui
              </button>
            )}
          </div>
        </article>
      ))}
      {rescheduleTarget !== null && (
        <AppointmentRescheduleModal
          appointmentId={rescheduleTarget.id}
          expectedVersion={rescheduleTarget.version}
          open
          onClose={() => setRescheduleTarget(null)}
        />
      )}
      {noShowTarget !== null && (
        <NoShowModal
          open
          appt={{ type: noShowTarget.type, title: noShowTarget.title }}
          submitting={markNoShow.isPending}
          onClose={() => setNoShowTarget(null)}
          onConfirm={({ reason, generatePendency }) => {
            markNoShow.mutate(
              {
                appointmentId: noShowTarget.id,
                expectedVersion: noShowTarget.version,
                body: { reason, generatePendency },
              },
              {
                onSuccess: (updated) => {
                  setNoShowTarget(null);
                  // O backend cria a Pendency de follow-up; dizer isso fecha o laço —
                  // senão o operador marca falta e não sabe que virou trabalho de alguém.
                  if (updated.noShowGeneratedPendencyId !== null) {
                    useToastStore.getState().push({
                      variant: 'success',
                      title: 'Falta registrada',
                      description: 'Uma pendência de retorno foi criada para você.',
                      durationMs: 5000,
                    });
                  }
                },
              },
            );
          }}
        />
      )}
    </>
  );
}
